/** What every Home reads and does: the data, the greeting, and marking a need done. The computer's and Season's pages share it. */
import { useCallback, useEffect, useState } from "react";
import type { HomeRange, HomeResponse } from "../../../shared/types";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import { useShownAgain } from "../../lib/inApp";
import { lastLoaded, load } from "../../lib/kept";

export const RANGE_KEY = "fr.home.range";
/** How often Right now asks again while Home is open. */
const REFRESH_MS = 30_000;

export function savedRange(): HomeRange {
  try {
    const v = localStorage.getItem(RANGE_KEY);
    return v === "7d" || v === "30d" || v === "all" ? v : "all";
  } catch {
    return "all";
  }
}

/** The hello for the host's own clock. */
export function greeting(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export interface HomeState {
  data: HomeResponse | null;
  failed: boolean;
  reload: () => void;
  setData: (f: (d: HomeResponse) => HomeResponse) => void;
}

export function useHome(range: HomeRange): HomeState {
  const key = `home:${range}`;
  const [data, setState] = useState<HomeResponse | null>(() => lastLoaded(key));
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);
  const shownAgain = useShownAgain();

  useEffect(() => {
    let alive = true;
    const shown = lastLoaded<HomeResponse>(key);
    if (shown) setState(shown);
    load(key, () => api.home(range))
      .then((d) => {
        if (!alive) return;
        setState(d);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [key, range, nonce, shownAgain]);

  useEffect(() => {
    const t = window.setInterval(() => document.visibilityState === "visible" && setNonce((n) => n + 1), REFRESH_MS);
    return () => window.clearInterval(t);
  }, []);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const setData = useCallback((f: (d: HomeResponse) => HomeResponse) => setState((d) => (d ? f(d) : d)), []);
  return { data, failed, reload, setData };
}

/** Marks a need or a fault done: it leaves at once, and comes back with a word if the save fails. */
export function useHomeDone({ setData, reload }: Pick<HomeState, "setData" | "reload">): (key: string, at: number) => Promise<void> {
  const toast = useToast();
  return useCallback(
    async (key, at) => {
      setData((d) => ({ ...d, needs: d.needs.filter((n) => n.key !== key), faults: d.faults.filter((f) => f.key !== key) }));
      try {
        await api.homeDone({ key, seen_at: at });
        toast("Marked as done");
      } catch {
        toast({ text: "That didn't save. Try again.", icon: "info" });
        reload();
      }
    },
    [setData, reload, toast],
  );
}
